import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CognitoTokenDTO, UserDTO, UserSessionDTO } from '../dtos';
import { AuthTenantDTO } from '../../manage/dtos';
import { MultiTenantAuthState } from '../types';
import { CognitoAuthService } from '../services/cognitoAuthService';
import { cognitoConfig } from '../../../config/cognito';
import { authApi } from '../api/authApi';
import { syncContextFromAuth } from '../../../stores/contextStore';

// Create a singleton instance of the auth service
const authService = new CognitoAuthService();

// Configure the auth service with Cognito settings
authService.configure({
  userPoolId: cognitoConfig.userPoolId,
  appClientId: cognitoConfig.userPoolWebClientId,
});

interface AuthStore extends MultiTenantAuthState {
  // Existing actions (enhanced)
  setAuth: (user: UserDTO, tokens?: CognitoTokenDTO, userSession?: UserSessionDTO) => void;
  clearAuth: () => void;
  setUser: (user: UserDTO) => void;
  setLoading: (loading: boolean) => void;
  updateTokens: (tokens: CognitoTokenDTO) => void;
  initializeAuth: () => Promise<void>;

  // Internal state tracking
  _isInitializing: boolean;
  _hasInitialized: boolean;
  _hasHydrated: boolean;
  _refreshFailures: number;

  // Multi-tenant actions
  setUserSession: (session: UserSessionDTO) => void;
  setCurrentTenant: (tenant: AuthTenantDTO) => void;
  setAccessibleTenants: (tenants: AuthTenantDTO[]) => void;
  setSwitchingTenant: (loading: boolean) => void;
  setTenantSwitchError: (error: string | null) => void;
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenantAccess: () => Promise<void>;
  refreshUserContext: () => Promise<void>;
  loadTenantContext: () => Promise<void>;

  // Convenience getters
  hasRole: (role: string) => boolean;
  getCurrentRoles: () => string[];
  canSwitchTenants: () => boolean;
  hasHydrated: () => boolean;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial auth state
      user: null,
      accessToken: null,
      idToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true, // Start as loading to prevent immediate redirect

      // Multi-tenant state
      currentTenant: null,
      accessibleTenants: [],
      userSession: null,
      isSwitchingTenant: false,
      tenantSwitchError: null,
      lastTenantRefresh: null,

      // Internal state tracking
      _isInitializing: false,
      _hasInitialized: false,
      _hasHydrated: false,
      _refreshFailures: 0,

      // Enhanced setAuth with tenant support
      setAuth: (user: UserDTO, tokens?: CognitoTokenDTO, userSession?: UserSessionDTO) => {
        // Note: Removed duplicate localStorage operations - Zustand persistence handles storage

        set((state) => {
          const newAccessibleTenants = userSession?.accessibleTenants || state.accessibleTenants;

          const newCurrentTenant = userSession
            ? newAccessibleTenants.find((t) => t.id === userSession.currentTenantId) || null
            : state.currentTenant;

          // FIX: Update user.id to DynamoDB userId when session is available
          // The user object initially has Cognito ID, but we need DynamoDB user.id for queries
          const updatedUser = userSession
            ? { ...user, id: userSession.userId } // Use DynamoDB user.id from session
            : user; // Keep Cognito ID if no session yet

          // Sync tenant context after setting auth
          const result = {
            user: updatedUser,
            isAuthenticated: true,
            accessToken: tokens?.accessToken || null,
            idToken: tokens?.idToken || null,
            refreshToken: tokens?.refreshToken || null,
            userSession,
            currentTenant: newCurrentTenant,
            accessibleTenants: newAccessibleTenants,
          };

          // Sync context store with current tenant
          try {
            syncContextFromAuth(newCurrentTenant?.id || null);
          } catch (error) {
            // Silently handle sync errors
          }

          return result;
        });
      },

      clearAuth: () => {
        // Note: Removed manual localStorage clearing - Zustand persistence handles this

        set({
          user: null,
          accessToken: null,
          idToken: null,
          refreshToken: null,
          isAuthenticated: false,
          // Clear multi-tenant state
          currentTenant: null,
          accessibleTenants: [],
          userSession: null,
          isSwitchingTenant: false,
          tenantSwitchError: null,
          lastTenantRefresh: null,
        });

        // Clear context store
        try {
          syncContextFromAuth(null);
        } catch (error) {
          // Silently handle clear errors
        }
      },

      setUser: (user: UserDTO) => {
        set({ user });
      },

      // Multi-tenant actions
      setUserSession: (session: UserSessionDTO) => {
        set((state) => {
          // FIX: Update user.id when session is updated
          // This ensures user.id stays in sync with the session's userId (DynamoDB ID)
          const updatedUser = state.user
            ? { ...state.user, id: session.userId } // Sync user.id with session userId
            : state.user;

          return {
            user: updatedUser,
            userSession: session,
            currentTenant:
              state.accessibleTenants.find((t) => t.id === session.currentTenantId) || null,
            accessibleTenants: session.accessibleTenants,
          };
        });
      },

      setCurrentTenant: (tenant: AuthTenantDTO) => {
        set({ currentTenant: tenant });
        // Sync context store with new tenant
        try {
          syncContextFromAuth(tenant?.id || null);
        } catch (error) {
          // Silently handle sync errors
        }
      },

      setAccessibleTenants: (tenants: AuthTenantDTO[]) => {
        set({ accessibleTenants: tenants });
      },

      setSwitchingTenant: (loading: boolean) => {
        set({ isSwitchingTenant: loading });
      },

      setTenantSwitchError: (error: string | null) => {
        set({ tenantSwitchError: error });
      },

      // Tenant switching functionality
      switchTenant: async (tenantId: string) => {
        const { setSwitchingTenant, setTenantSwitchError, setUserSession } = get();

        try {
          setSwitchingTenant(true);
          setTenantSwitchError(null);

          const response = await authApi.switchTenant({ targetTenantId: tenantId });

          if (response.success && response.newSession) {
            setUserSession(response.newSession);
          } else {
            throw new Error(response.message || 'Failed to switch tenant');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to switch tenant';
          setTenantSwitchError(errorMessage);
          throw error;
        } finally {
          setSwitchingTenant(false);
        }
      },

      // Refresh tenant access information
      refreshTenantAccess: async () => {
        try {
          const response = await authApi.getAccessibleTenants();
          const { setAccessibleTenants } = get();

          setAccessibleTenants(response.tenants);
          set({ lastTenantRefresh: new Date().toISOString() });
        } catch (error) {
          console.error('Failed to refresh tenant access:', error);
          throw error;
        }
      },

      // Refresh user context (roles, permissions, etc.)
      refreshUserContext: async () => {
        try {
          const response = await authApi.refreshSessionContext();

          if (response.success && response.updatedSession) {
            const { setUserSession } = get();
            setUserSession(response.updatedSession);
          }
        } catch (error) {
          console.error('Failed to refresh user context:', error);
          throw error;
        }
      },

      // Load tenant context after authentication
      loadTenantContext: async () => {
        try {
          // Load current session with tenant context
          const session = await authApi.getCurrentSession();
          const { setUserSession } = get();

          setUserSession(session);
          set({ lastTenantRefresh: new Date().toISOString() });
        } catch (error) {
          console.error('Failed to load tenant context:', error);
          // Don't throw - this is optional enhancement, fallback to basic auth
        }
      },

      // Convenience methods
      hasRole: (role: string) => {
        const { userSession } = get();
        return userSession?.currentRoles?.some((userRole) => userRole.toString() === role) ?? false;
      },

      getCurrentRoles: () => {
        const { userSession } = get();
        return userSession?.currentRoles?.map((role) => role.toString()) ?? [];
      },

      canSwitchTenants: () => {
        const { accessibleTenants } = get();
        return accessibleTenants.length > 1;
      },

      hasHydrated: () => {
        const { _hasHydrated } = get();
        return _hasHydrated;
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      updateTokens: (tokens: CognitoTokenDTO) => {
        set({
          accessToken: tokens.accessToken,
          idToken: tokens.idToken,
          refreshToken: tokens.refreshToken,
        });
      },

      initializeAuth: async () => {
        const state = get();

        // Prevent multiple concurrent initializations
        if (state._isInitializing) {
          return;
        }

        // If already initialized, skip
        if (state._hasInitialized) {
          return;
        }

        // CRITICAL: Wait for hydration to complete before proceeding
        if (!state._hasHydrated) {
          return;
        }

        set({ isLoading: true, _isInitializing: true });

        try {
          // Check if we have stored tokens in the auth store
          const state = get();
          const hasStoredTokens = !!(state.refreshToken && state.idToken);

          if (hasStoredTokens) {
            // Configure auth service with stored tokens
            authService.restoreSession({
              accessToken: state.accessToken || '',
              idToken: state.idToken || '',
              refreshToken: state.refreshToken || '',
              username: state.user?.email || '',
            });

            // Only refresh tokens if they expire within 10 minutes (as originally requested)
            try {
              if (authService.isSessionNearExpiry()) {
                await authService.refreshSession();
              }

              // Load user info (with current or refreshed tokens)
              const userInfo = await authService.loadUser();

              // Convert UserInfoDTO to UserDTO for store compatibility
              const user: UserDTO = {
                id: userInfo.id,
                email: userInfo.email,
                name: userInfo.name,
                cognitoId: userInfo.id, // Cognito user ID from the 'sub' attribute
              };

              // Get current tokens from the auth service (refreshed)
              const currentTokens = authService.getCurrentTokens();
              const tokens: CognitoTokenDTO = {
                accessToken: currentTokens?.accessToken || '',
                idToken: currentTokens?.idToken || '',
                refreshToken: currentTokens?.refreshToken || '',
              };

              // Try to load tenant context - but don't fail auth if it fails
              let userSession: UserSessionDTO | undefined;
              try {
                userSession = await authApi.getCurrentSession();
              } catch (tenantError) {
                console.error('Failed to load tenant context:', tenantError);
              }

              get().setAuth(user, tokens, userSession);

              // Reset refresh failures on successful auth
              set({ _refreshFailures: 0 });
            } catch (refreshError) {
              // Track refresh failures
              const currentFailures = get()._refreshFailures + 1;
              set({ _refreshFailures: currentFailures });

              // If we've failed multiple times, force clear localStorage in development
              if (currentFailures >= 2 && import.meta.env.DEV) {
                localStorage.removeItem('auth-store');
                window.location.reload();
                return;
              }

              // CRITICAL: Immediately clear localStorage to prevent refresh loops
              localStorage.removeItem('auth-store');

              // Also clear sessionStorage as a backup
              try {
                sessionStorage.removeItem('auth-store');
              } catch (e) {
                // Ignore sessionStorage errors
              }

              // Only force reload in development after multiple failures
              if (import.meta.env.DEV && currentFailures >= 2) {
                setTimeout(() => window.location.reload(), 100);
                return;
              }

              get().clearAuth();
            }
          } else {
            get().clearAuth();
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          get().clearAuth();
        } finally {
          set({ isLoading: false, _isInitializing: false, _hasInitialized: true });
        }
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        idToken: state.idToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated, // Persist isAuthenticated to maintain state across refreshes
        // Multi-tenant state persistence
        currentTenant: state.currentTenant,
        accessibleTenants: state.accessibleTenants,
        userSession: state.userSession,
        lastTenantRefresh: state.lastTenantRefresh,
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('AuthStore: Hydration failed:', error);
            // Set loading to false on hydration error
            if (state) {
              state._hasHydrated = true;
              state.isLoading = false;
            }
          } else {
            // Mark as hydrated - AuthProvider will handle initialization
            if (state) {
              state._hasHydrated = true;
              // Keep isLoading true if we have stored tokens (AuthProvider will initialize)
              // Set isLoading false if no stored tokens (user should be redirected to login)
              const hasStoredTokens = !!(state.refreshToken && state.idToken);
              if (!hasStoredTokens) {
                state.isLoading = false;
              }
            }
          }
        };
      },
    }
  )
);

// Setup token synchronization between auth service and store
authService.onTokenUpdate((tokens) => {
  useAuthStore.getState().updateTokens({
    accessToken: tokens.accessToken || '',
    idToken: tokens.idToken || '',
    refreshToken: tokens.refreshToken || '',
  });
});

// Export the auth service instance for use in other parts of the app
export { authService };
